import React, { useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import {
  Box,
  Grid,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  uniqueNamesGenerator,
  adjectives,
  animals,
} from 'unique-names-generator';
import {
  DbQuestionData,
  DetailedPlayer,
  Maintenance,
} from '../components/room/types';
import { fetchGet } from '../api/common';
import { StyledPaperItem } from '../common/mui-components';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import FlareIcon from '@mui/icons-material/Flare';
import DiamondIcon from '@mui/icons-material/Diamond';
import { TbAtom } from 'react-icons/tb';
import RocketIcon from '@mui/icons-material/Rocket';
import { utcToLocalDateTime } from '../components/room/utils';
import { ScoreCardModal } from '../modals/ScoreCardModal';
import { CATEGORY_SETS_LABEL } from '../common/constants';
import { MEDAL_COLORS, MEDAL_REQUIREMENTS } from '@par/shared/common/constants';
import { GameDb, PunchcardCategory } from '@par/shared/common/types';
import { sum } from '@par/shared/utils/math';
import { Profile } from '@par/shared/types/player';

const nameGenConfig = {
  dictionaries: [adjectives, animals],
  separator: ' ',
  length: 2,
};

type NextTrophyReqs = {
  medal: string;
  value: number;
  scoreNeededToAchieve: number;
};

type PropTypes = {
  categorySet: string;
  close: () => void;
  gameType: 'casual' | 'pro';
  maintenance: Maintenance;
  onPlaySoloFromPunchcard: (
    gameType: 'casual' | 'pro',
    categorySet: string,
    customGameName?: string
  ) => void;
  open: boolean;
  profileId: string;
  viewProfile: Profile;
};

export const SoloPunchcardDetailsDialog = ({
  categorySet,
  close,
  gameType,
  maintenance,
  onPlaySoloFromPunchcard,
  open,
  profileId,
  viewProfile,
}: PropTypes) => {
  const stats = viewProfile.soloStats[gameType][categorySet];
  const theme = useTheme();
  const [customGameName, setCustomGameName] = useState<string>();
  const [bestGame, setBestGame] = useState<GameDb>();
  const [bestAverageGames, setBestAverageGames] = useState<GameDb[]>();
  const [recentAverageGames, setRecentAverageGames] = useState<GameDb[]>();
  const [nextTophyReqs, setNextTrophyReqs] = useState<NextTrophyReqs[]>([]);
  const [gameRow, setGameRow] = useState<GameDb>();
  const [questionSet, setQuestionSet] = useState<DbQuestionData[]>();
  const [openScorecardModal, setOpenScorecardModal] = useState(false);
  const [recentBestTimeToggle, setRecentBestTimeToggle] = useState(false);

  useEffect(() => {
    if (!open) return;
    const generatedcustomGameName = uniqueNamesGenerator({
      ...nameGenConfig,
      style: 'capital',
    });
    setCustomGameName(generatedcustomGameName);
    fetchPunchcardGames();
    nextBestAvgReqs(stats.average);
  }, [open]);

  const fetchPunchcardGames = async () => {
    const res = await fetchGet(
      `/games/punchcard/${viewProfile.id}/${gameType}/${categorySet}`
    );
    if (!res.bestAverageGames) return;
    setBestGame(res.bestGame);
    setBestAverageGames(res.bestAverageGames);
    setRecentAverageGames(
      res.recentAverageGames.sort((a, b) => a.createdAt - b.createdAt)
    );
  };

  const nextBestAvgReqs = (currentAvg: number) => {
    const medalKeys = Object.keys(MEDAL_REQUIREMENTS.average);
    const remainingMedals = [];
    for (let i = medalKeys.length - 1; i > 0; i--) {
      if (currentAvg < MEDAL_REQUIREMENTS.average[medalKeys[i]]) {
        break;
      } else {
        remainingMedals.push({
          medal: medalKeys[i],
          value: MEDAL_REQUIREMENTS.average[medalKeys[i]],
        });
      }
    }
    const lastNineScores = stats.lastTenScores.slice(
      1,
      stats.lastTenScores.length
    );

    for (const rm of remainingMedals) {
      const scoreNeededToAchieve = rm.value * 10 - sum(lastNineScores);
      rm.scoreNeededToAchieve = scoreNeededToAchieve;
    }
    setNextTrophyReqs(remainingMedals.reverse());
  };

  const getMedal = (punchcardCategory: PunchcardCategory, value: number) => {
    const medalCategory = MEDAL_REQUIREMENTS[punchcardCategory];

    if (
      (punchcardCategory === 'bestAverage' ||
        punchcardCategory === 'average') &&
      !bestAverageGames
    ) {
      return (
        <EmojiEventsIcon
          sx={{
            color: 'black',
            fontSize: 40,
          }}
        />
      );
    }

    if (
      punchcardCategory === 'gamesPlayed'
        ? value >= medalCategory.antimatter
        : value <= medalCategory.antimatter
    ) {
      return (
        <TbAtom
          size={40}
          color={MEDAL_COLORS.antimatter}
          style={{ boxShadow: '1px 1px 15px #FFF, 1px 1px 5px #fffefe' }}
        />
      );
    }
    if (
      punchcardCategory === 'gamesPlayed'
        ? value >= medalCategory.unobtainium
        : value <= medalCategory.unobtainium
    ) {
      return (
        <RocketIcon sx={{ color: MEDAL_COLORS.unobtainium, fontSize: 40 }} />
      );
    }
    if (
      punchcardCategory === 'gamesPlayed'
        ? value >= medalCategory.diamond
        : value <= medalCategory.diamond
    ) {
      return (
        <DiamondIcon
          sx={{
            color: MEDAL_COLORS.diamond,
            fontSize: 40,
          }}
        />
      );
    }
    if (
      punchcardCategory === 'gamesPlayed'
        ? value >= medalCategory.platinum
        : value <= medalCategory.platinum
    ) {
      return (
        <FlareIcon
          sx={{
            color: MEDAL_COLORS.platinum,
            fontSize: 40,
          }}
        />
      );
    }
    if (
      punchcardCategory === 'gamesPlayed'
        ? value >= medalCategory.gold
        : value <= medalCategory.gold
    ) {
      return (
        <EmojiEventsIcon
          sx={{
            color: MEDAL_COLORS.gold,
            fontSize: 40,
          }}
        />
      );
    }
    if (
      punchcardCategory === 'gamesPlayed'
        ? value >= medalCategory.silver
        : value <= medalCategory.silver
    ) {
      return (
        <EmojiEventsIcon
          sx={{
            color: MEDAL_COLORS.silver,
            fontSize: 40,
          }}
        />
      );
    }
    if (
      punchcardCategory === 'gamesPlayed'
        ? value >= medalCategory.bronze
        : value <= medalCategory.bronze
    ) {
      return (
        <EmojiEventsIcon
          sx={{
            color: MEDAL_COLORS.bronze,
            fontSize: 40,
          }}
        />
      );
    }
    return (
      <EmojiEventsIcon
        sx={{
          color: 'black',
          fontSize: 40,
        }}
      />
    );
  };

  const handleViewResultsClick = async (game: GameDb) => {
    const res = await fetchGet(`/games/result/${game.gameId}`);
    setGameRow(res.game);
    setQuestionSet(res.questionSet);
    setOpenScorecardModal(true);
  };

  return (
    <>
      {gameRow && questionSet ? (
        <ScoreCardModal
          onClose={() => setOpenScorecardModal(false)}
          categorySet={gameRow.categorySet}
          gameId={gameRow.gameId}
          gameType={gameRow.gameType as 'casual' | 'pro'}
          isDaily={gameRow.daily}
          openScordcardModal={openScorecardModal}
          mode={gameRow.mode}
          players={gameRow.players as Record<string, DetailedPlayer>}
          profileId={viewProfile.id}
          questionSet={questionSet}
          ranked={gameRow.ranked}
        ></ScoreCardModal>
      ) : null}
      <Dialog open={open} onClose={close} maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography variant="h5" textAlign={'center'} letterSpacing={5}>
            {gameType.toUpperCase()} |{' '}
            {CATEGORY_SETS_LABEL[categorySet].toUpperCase()}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ width: '100%', height: '100%' }}>
          <Box
            sx={{
              position: 'relative',
              flexGrow: 1,
            }}
          >
            {viewProfile.id === profileId ? (
              <>
                {maintenance.scheduled ? (
                  <Typography
                    color="error.main"
                    textAlign={'center'}
                    sx={{ mb: 1 }}
                  >
                    PAR is under maintenance. Games cannot be played right now
                  </Typography>
                ) : null}
                <Stack
                  direction={'row'}
                  spacing={5}
                  sx={{
                    mb: 2,
                    padding: 2,
                    border: '1px solid',
                    borderColor: theme.palette.secondary.main,
                    borderRadius: '5px',
                  }}
                >
                  <Box sx={{ width: '50%' }}>
                    <TextField
                      id="customGameName"
                      label="Game Name"
                      variant="standard"
                      value={customGameName}
                      fullWidth
                      onChange={(e) => setCustomGameName(e.target.value)}
                    />
                  </Box>
                  <Box sx={{ width: '50%' }}>
                    <Button
                      variant="contained"
                      onClick={() =>
                        onPlaySoloFromPunchcard(
                          gameType,
                          categorySet,
                          customGameName
                        )
                      }
                      fullWidth
                      sx={{ height: '100%' }}
                      disabled={maintenance.scheduled}
                    >
                      PLAY SOLO GAME
                    </Button>
                  </Box>
                </Stack>
              </>
            ) : null}
            <Grid container spacing={2} className="CONTAINER-MAIN">
              <Grid item xs={12} sm={12} className="CONTAINER-TOP">
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={12} className="MAIN-STATS">
                    <Grid container spacing={2}>
                      <Grid item xs={3} sm={3} className="CURRENT-AVG">
                        <StyledPaperItem
                          sx={{
                            paddingX: 2,
                          }}
                        >
                          <Box sx={{ color: '#B0E0E6' }}>
                            {getMedal('average', stats.average)}
                            <Typography variant="h6">Current Avg</Typography>
                            <Typography variant="h6">
                              {stats.average > 0 ? '+' : ''}
                              {stats.average || '--'}
                            </Typography>
                          </Box>
                        </StyledPaperItem>
                      </Grid>
                      <Grid item xs={3} sm={3} className="BEST-AVG">
                        <StyledPaperItem
                          sx={{
                            paddingX: 2,
                          }}
                        >
                          <Box sx={{ color: '#53ecff' }}>
                            {getMedal('bestAverage', stats.bestAverage)}
                            <Typography variant="h6">Best Avg</Typography>
                            <Typography variant="h6">
                              {stats.bestAverage > 0 ? '+' : ''}
                              {stats.bestAverage || '--'}
                            </Typography>
                          </Box>
                        </StyledPaperItem>
                      </Grid>
                      <Grid item xs={3} sm={3} className="BEST-GAME">
                        <StyledPaperItem
                          sx={{
                            paddingX: 2,
                          }}
                        >
                          <Box sx={{ color: '#00FF00' }}>
                            {getMedal('best', stats.best)}
                            <Typography variant="h6">Best Game</Typography>
                            <Typography variant="h6">
                              {stats.best > 0 ? '+' : ''}
                              {stats.best || '--'}
                            </Typography>
                          </Box>
                        </StyledPaperItem>
                      </Grid>
                      <Grid item xs={3} sm={3} className="GAMES-PLAYED">
                        <StyledPaperItem
                          sx={{
                            paddingX: 2,
                          }}
                        >
                          <Box sx={{ color: '#EE82EE' }}>
                            {getMedal('gamesPlayed', stats.gamesPlayed)}
                            <Typography variant="h6">Games Played</Typography>
                            <Typography variant="h6">
                              {stats.gamesPlayed || '--'}
                            </Typography>
                          </Box>
                        </StyledPaperItem>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={12} sm={12} className="CURRENT-AVG-GOALS">
                    <StyledPaperItem
                      sx={{
                        paddingX: 2,
                      }}
                    >
                      <Stack spacing={2} mb={1}>
                        <Typography variant="h6" sx={{ color: '#B0E0E6' }}>
                          Based on your current avg, score needed in next game
                          to achieve:
                        </Typography>
                        <Stack
                          direction="row"
                          spacing={6}
                          sx={{ display: 'flex', justifyContent: 'center' }}
                        >
                          {stats.lastTenScores.length >= 9 ? (
                            nextTophyReqs && nextTophyReqs.length ? (
                              nextTophyReqs.map((t) => (
                                <Stack spacing={1}>
                                  {getMedal('average', t.value)}
                                  <Typography
                                    variant="h6"
                                    sx={{
                                      color: theme.palette.dailies.main,
                                    }}
                                  >
                                    {t.scoreNeededToAchieve > 0 ? '+' : ''}
                                    {t.scoreNeededToAchieve}
                                  </Typography>
                                </Stack>
                              ))
                            ) : (
                              <Typography>
                                You've reached the PAR pinnacle!
                              </Typography>
                            )
                          ) : (
                            <Typography>
                              Play 9 games to see what you need on the 10th
                            </Typography>
                          )}
                        </Stack>
                      </Stack>
                    </StyledPaperItem>
                  </Grid>
                  <Grid item xs={12} sm={12} className="BEST-CONSECUTIVE-GAMES">
                    <StyledPaperItem
                      sx={{
                        paddingX: 2,
                      }}
                    >
                      <Stack mb={1}>
                        <Stack
                          direction="row"
                          spacing={2}
                          sx={{
                            width: '100%',
                            color: 'dailies.main',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                          }}
                        >
                          <Typography>Recent</Typography>
                          <Switch
                            checked={recentBestTimeToggle}
                            onChange={() =>
                              setRecentBestTimeToggle((prev) => !prev)
                            }
                          />
                          <Typography>Best</Typography>
                        </Stack>
                        <Typography
                          variant="h6"
                          sx={{ mb: 2, color: '#53ecff' }}
                        >
                          Consecutive 10 Games
                        </Typography>
                        {recentAverageGames && bestAverageGames ? (
                          <Stack
                            direction="row"
                            spacing={2}
                            sx={{ display: 'flex', justifyContent: 'center' }}
                          >
                            {(recentBestTimeToggle
                              ? bestAverageGames
                              : recentAverageGames
                            ).map((g) => (
                              <Tooltip
                                title={utcToLocalDateTime(g.createdAt)}
                                placement="top"
                              >
                                <Button
                                  variant="outlined"
                                  onClick={() => handleViewResultsClick(g)}
                                >
                                  <Typography variant="h6">
                                    {g.players[viewProfile.id]?.parScore > 0
                                      ? '+'
                                      : ''}
                                    {g.players[viewProfile.id]?.parScore}
                                  </Typography>
                                </Button>
                              </Tooltip>
                            ))}
                          </Stack>
                        ) : (
                          <Typography>Minimum 10 games required</Typography>
                        )}
                      </Stack>
                    </StyledPaperItem>
                  </Grid>
                  <Grid item xs={12} sm={12} className="BEST-GAME-SCORE">
                    <StyledPaperItem
                      sx={{
                        paddingX: 2,
                      }}
                    >
                      <Stack spacing={2} mb={1}>
                        <Typography variant="h6" sx={{ color: '#00FF00' }}>
                          Best Game Score
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                          {bestGame ? (
                            <Tooltip
                              title={utcToLocalDateTime(bestGame.createdAt)}
                              placement="top"
                            >
                              <Button
                                variant="outlined"
                                onClick={() => handleViewResultsClick(bestGame)}
                              >
                                <Typography variant="h6">
                                  {bestGame.players[viewProfile.id]?.parScore >
                                  0
                                    ? '+'
                                    : ''}
                                  {bestGame.players[viewProfile.id]?.parScore}
                                </Typography>
                              </Button>
                            </Tooltip>
                          ) : (
                            <Typography>No games played yet</Typography>
                          )}
                        </Box>
                      </Stack>
                    </StyledPaperItem>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={close} fullWidth>
            CLOSE
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
